import React from "react";
import { motion } from "framer-motion";
import { fadeIn, staggerContainer } from "../variants";
import { CheckCircle2, BookOpen, Zap, Users, Hammer } from "lucide-react";

const currentObjectives = [
  "Construction d'une salle pour les cours d'alphabétisation",
  "Construction d'une salle de formation en couture",
  "Construction d'une bibliothèque",
  "Équipement en panneaux solaires",
];

const pastProject = [
  "Construction d'un bâtiment (189m²) de 3 classes en dur",
  "Forage d'eau potable",
  "Construction de latrines",
  "École de proximité favorisant la scolarisation des filles",
];

const funders = [
  { name: "FORIM / PRA-OSIM", color: "bg-blue-50 text-blue-700" },
  { name: "Agence Française de Développement", color: "bg-green-50 text-green-700" },
  { name: "Fondation Devoteam (15 000 €)", color: "bg-orange-50 text-orange-700" },
];

const ModeleEcole = () => {
  return (
    <section id="projet-modele" className="section scroll-mt-20">
      <div className="container mx-auto">

        {/* Header */}
        <motion.div
          variants={staggerContainer(0.12, 0.1)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="section-header"
        >
          <motion.p variants={fadeIn("up")} className="eyebrow mb-4">
            Projet 2 — Modélé, Canton, Tchad
          </motion.p>
          <motion.h2 variants={fadeIn("up", 0.05)} className="h2 mb-6">
            Alphabétisation et formation des femmes
          </motion.h2>
          <motion.p variants={fadeIn("up", 0.1)} className="lead">
            En 2023, lors d'ateliers de sensibilisation, les femmes de Modélé nous ont dit :
            <em className="text-secondary font-medium">
              {" "}« Nous avons honte de ne pas savoir parler français devant les étrangers. »
            </em>{" "}
            Leur parole a tout changé.
          </motion.p>
        </motion.div>

        {/* Quote block */}
        <motion.div
          variants={fadeIn("up")}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="relative bg-primary text-white rounded-2xl p-8 lg:p-12 mb-16 overflow-hidden"
        >
          <div className="absolute top-4 right-6 text-9xl font-heading text-white/5 leading-none select-none">
            "
          </div>
          <div className="relative max-w-3xl">
            <p className="text-xl lg:text-2xl font-heading leading-relaxed mb-6">
              Les plus âgées de ces femmes regrettent de n'avoir pas encouragé leurs filles à aller
              à l'école. C'est comme si elles leur avaient créé toutes les possibilités de vivre
              le même calvaire.
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-0.5 bg-secondary" />
              <span className="text-white/70 text-sm">Ateliers de sensibilisation, Modélé 2023</span>
            </div>
          </div>
        </motion.div>

        {/* Two-column projects */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">

          {/* ABC Project — en cours */}
          <motion.div
            variants={fadeIn("right")}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="card p-8"
          >
            <div className="flex items-center gap-2 mb-6">
              <span className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
              <span className="text-secondary font-semibold text-xs uppercase tracking-widest">
                Recherche de financements
              </span>
            </div>
            <h3 className="h3 mb-4">Projet ABC 2025–2027</h3>
            <p className="text-grey text-sm leading-relaxed mb-6">
              Alphabétisation, Bibliothèque, Couture — formation des femmes et des jeunes filles-mères.
              Budget prévisionnel : <strong className="text-dark">32 000 €</strong>
            </p>

            <div className="space-y-3 mb-6">
              {currentObjectives.map((obj, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <CheckCircle2 size={16} className="text-secondary flex-shrink-0 mt-0.5" />
                  <span className="text-dark text-sm">{obj}</span>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-3">
              {[
                { icon: <BookOpen size={16} />, label: "Alphabétisation" },
                { icon: <Users size={16} />, label: "Couture" },
                { icon: <Zap size={16} />, label: "Solaire" },
              ].map((t, i) => (
                <div key={i} className="bg-secondary/5 rounded-xl p-3 text-center">
                  <div className="text-secondary mx-auto mb-1.5 flex justify-center">{t.icon}</div>
                  <div className="text-xs font-medium text-dark">{t.label}</div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Agrandissement project */}
          <motion.div
            variants={fadeIn("left")}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="card p-8"
          >
            <div className="flex items-center gap-2 mb-6">
              <span className="w-2 h-2 rounded-full bg-accent" />
              <span className="text-accent font-semibold text-xs uppercase tracking-widest">
                En planification
              </span>
            </div>
            <h3 className="h3 mb-4">Agrandissement de l'école</h3>
            <p className="text-grey text-sm leading-relaxed mb-6">
              Construction d'un bâtiment de 2 salles de classes supplémentaires et réhabilitation
              de la toiture du bâtiment actuel (3 classes exposées aux intempéries).
            </p>

            <div className="space-y-3">
              {[
                "Construire 2 classes en dur pour tout temps",
                "Créer un pôle éducatif sécurisé de proximité",
                "Renforcer la motivation à scolariser les filles",
                "Réparer la toiture existante avant écroulement",
              ].map((obj, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <Hammer size={16} className="text-accent flex-shrink-0 mt-0.5" />
                  <span className="text-dark text-sm">{obj}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-accent/5 rounded-xl border border-accent/10">
              <p className="text-xs text-grey">
                <span className="font-semibold text-accent">ODD n°4</span> — Ce projet s'inscrit
                dans les Objectifs de Développement Durable : éducation équitable et de qualité pour tous d'ici 2030.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Past project */}
        <motion.div
          variants={fadeIn("up")}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="bg-light rounded-2xl p-8 lg:p-12 border border-gray-100"
        >
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="eyebrow mb-4 block">Réalisé 2019–2020</span>
              <h3 className="h3 mb-6">
                Construction de l'école de Modélé
              </h3>
              <p className="text-grey text-sm leading-relaxed mb-6">
                Grâce aux soutiens du FORIM, de l'AFD et de la Fondation Devoteam, nous avons
                construit la première infrastructure scolaire durable du village de Modélé/Mandoul.
              </p>
              <div className="space-y-3">
                {pastProject.map((item, i) => (
                  <div key={i} className="flex gap-3 items-start">
                    <CheckCircle2 size={16} className="text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-dark text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-dark text-sm mb-4">Partenaires financiers</h4>
              <div className="space-y-3">
                {funders.map((f, i) => (
                  <div
                    key={i}
                    className={`px-4 py-3 rounded-xl text-sm font-medium ${f.color}`}
                  >
                    {f.name}
                  </div>
                ))}
              </div>
              <div className="mt-6 bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="text-xs text-grey mb-1">Impact direct</div>
                <div className="font-heading font-bold text-2xl text-primary">250+</div>
                <div className="text-sm text-grey">élèves bénéficiaires depuis 2020</div>
              </div>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default ModeleEcole;
